import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import styled, { keyframes } from "styled-components/macro";
import { FavoritesList } from "./FavoritesList";
import { OverlayImageContainer } from "../Image/OverlayImageContainer";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import { buildStatelessConfigImageUrl } from "../../utilities/configImageUrl";
import { ACTIVATE_FAVORITE } from "../../store/actions/configurationActions";
import {
    getAccount,
    getAppConfig,
    getFavoriteOverlayActive,
    getLanguage,
    getLocalization,
} from "../../store/sagas/selectors";
import { ConfigImageContainer } from "../Image/ConfigImageContainer";
import { ConfigImage } from "../Image/ConfigImage";

const onLoadAnimation = keyframes`
    0% {bottom: 30px; opacity:0;}
    100% {bottom: 40px; opacity:1;}
`;

const onLoadAnimationMobile = keyframes`
    0% {opacity:0;}
    100% {opacity:1;}
`;

const Container = styled.div<ContainerProps>`
    display: ${props => (props.active ? "inline-block" : "none")};
    @media only screen and (max-width: 896px) {
        width: 100%;
        height: 100%;
        position: absolute;
        z-index: 100;
        background: rgb(247, 244, 240);
        top: 0;
    } ;
`;

const ButtonContainer = styled.div<ButtonContainerProps>`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: absolute;
    z-index: 5;
    animation-name: ${props => (props.isMobile ? onLoadAnimationMobile : onLoadAnimation)};
    animation-duration: 0.5s;
    animation-timing-function: ease-in-out;
    bottom: ${props => (props.isMobile ? "6px" : "40px")};
    ${({ theme }) => theme.bp.xxxl`
        transform: scale(1.4, 1.4);
        transform-origin: bottom center;
    `}
`;

const Button = styled.button<ButtonProps>`
    font-family: ${props => props.theme.fontFamily};
    font-size: 16px;
    color: ${props => (props.primary ? "#fff" : "#000")};
    padding: 10px 20px;
    background: ${props => (props.primary === "primary" ? "var(--primary-color)" : "var(--light-active-color)")};
    margin: 10px;
    border-radius: 20px;
    border: none;
    box-shadow: 0px 3px 10px 0 rgb(0 0 0 / 20%);
    transition: background 0.3s ease-in-out;
    &:hover {
        background: ${props =>
            props.primary === "primary" ? "var(--primary-hover-color)" : "var(--light-hover-color)"};
        cursor: pointer;
    }
    &:disabled {
        background: #b9b9b9;
        cursor: context-menu;
        color: #626262;
    }
`;

interface ButtonContainerProps {
    isMobile: Boolean;
}

interface ButtonProps {
    primary?: string;
}

interface ContainerProps {
    active: Boolean;
}

export const FavoritesOverlay = ({
    favorites,
    onClose,
    onLoad,
    isActive,
    currentConfig,
}: {
    favorites: string[];
    onClose: Function;
    onLoad: Function;
    isActive: Boolean;
    currentConfig: string;
}) => {
    const dispatch = useDispatch();
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const account = useSelector(getAccount);
    const market = useSelector(getLocalization);
    const overlayActive = useSelector(getFavoriteOverlayActive);
    const [removedFavorites, setRemovedFavorites] = useState<string[]>([]);
    const initialImage = favorites.indexOf(currentConfig) > -1 ? currentConfig : favorites[0];
    const [imageUrl, setImageUrl] = useState<string | undefined>(initialImage);
    const [loading, setLoading] = useState(false);
    const configImageRef = useRef<HTMLImageElement>(null);

    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;

    useEffect(() => {
        setLoading(false);
        if (!overlayActive) {
            setRemovedFavorites([]);
        }
    }, [overlayActive]);

    const loadConfiguration = () => {
        dispatch({ type: ACTIVATE_FAVORITE, configurationString: imageUrl });
        setLoading(true);
        const changed = favoriteConfigurationChanged();
        onLoad(removedFavorites, changed);
    };

    const setActive = (config: string) => {
        setImageUrl(config);
    };

    const ToggleFavorites = (config: string) => {
        const tempArr = [...removedFavorites];
        const index = tempArr.indexOf(config);
        if (index > -1) {
            tempArr.splice(index, 1);
            setRemovedFavorites(tempArr);
            return;
        }

        tempArr.push(config);
        setRemovedFavorites(tempArr);
    };

    const favoriteConfigurationChanged = () => {
        const changed = initialImage !== imageUrl;
        return changed;
    };

    const imageWidth = configImageRef.current?.naturalWidth;
    const imageHeight = configImageRef.current?.naturalHeight;
    return (
        <Container active={isActive}>
            {!isMobile ? (
                <OverlayImageContainer>
                    <ConfigImageContainer imageWidth={imageWidth} imageHeight={imageHeight}>
                        <ConfigImage
                            ref={configImageRef}
                            src={
                                imageUrl
                                    ? buildStatelessConfigImageUrl(imageUrl, {
                                          scaledHeight: window.innerHeight,
                                          ext: "jpg",
                                          allowUpscaling: false,
                                          accountId: account?.id,
                                          market,
                                      })
                                    : ""
                            }
                            alt=""
                        />
                    </ConfigImageContainer>
                </OverlayImageContainer>
            ) : null}
            <FavoritesList
                favorites={favorites}
                onClick={setActive}
                toggleFavorites={ToggleFavorites}
                removedFavorites={removedFavorites}
                currentConfig={currentConfig}
            />
            <ButtonContainer isMobile={isMobile}>
                <Button
                    primary="primary"
                    disabled={loading}
                    onClick={() => {
                        loadConfiguration();
                    }}
                >
                    {!loading
                        ? get(appConfig, `translations.${language}.loadFavorite`, "loadFavorite")
                        : get(appConfig, `translations.${language}.loading`, "Loading ...")}
                </Button>
                <Button
                    onClick={() => {
                        onClose(removedFavorites);
                    }}
                >
                    {get(appConfig, `translations.${language}.closeFavorite`, "closeFavorite")}
                </Button>
            </ButtonContainer>
        </Container>
    );
};
